/**
 * Real damage photos of the public demo. The files have one home —
 * apps/web/public/demo-photos, where the web demo serves them — and the app
 * bundles the same files (apps/mobile/src/demo/demoPhotos.ts). Provenance is
 * in that folder's SOURCES.md; `apps/web/src/test/demoPhotos.test.ts` holds
 * that every file is listed there, carries no metadata segment (no EXIF/GPS),
 * and has exactly the size recorded here.
 *
 * The seed stores a reference, never a URL: `photo:<name>` for the full image,
 * `photo:<name>:thumb` for the thumbnail. The demo store resolves it when it
 * answers, through the platform's resolver (web: the public path, app: the
 * bundled asset), like `placeholder:` refs (placeholderPhoto.ts).
 */

export interface DemoPhoto {
  /** File name of the full image (longest side ≤ 1600 px). */
  readonly file: string;
  /** File name of the thumbnail (longest side 400 px). */
  readonly thumb: string;
  /** Size of the full image in pixels — what the viewer lays out against. */
  readonly width: number;
  readonly height: number;
}

export const DEMO_PHOTOS = {
  'vorderkante-kratzer': { file: 'vorderkante-kratzer.jpg', thumb: 'vorderkante-kratzer-thumb.jpg', width: 1280, height: 718 },
  'fluegel-lackabplatzer': { file: 'fluegel-lackabplatzer.jpg', thumb: 'fluegel-lackabplatzer-thumb.jpg', width: 1280, height: 853 },
  'tankdeckel-dichtung': { file: 'tankdeckel-dichtung.jpg', thumb: 'tankdeckel-dichtung-thumb.jpg', width: 960, height: 1280 },
  'cowling-lackabplatzer': { file: 'cowling-lackabplatzer.jpg', thumb: 'cowling-lackabplatzer-thumb.jpg', width: 960, height: 1280 },
  'radverkleidung-lack': { file: 'radverkleidung-lack.jpg', thumb: 'radverkleidung-lack-thumb.jpg', width: 960, height: 1280 },
  'cowling-fuge-abplatzer': { file: 'cowling-fuge-abplatzer.jpg', thumb: 'cowling-fuge-abplatzer-thumb.jpg', width: 1280, height: 1109 },
  'kratzer-oberseite': { file: 'kratzer-oberseite.jpg', thumb: 'kratzer-oberseite-thumb.jpg', width: 960, height: 1280 },
  'hagel-cowling': { file: 'hagel-cowling.jpg', thumb: 'hagel-cowling-thumb.jpg', width: 1200, height: 799 },
  'vorderkante-macke': { file: 'vorderkante-macke.jpg', thumb: 'vorderkante-macke-thumb.jpg', width: 1280, height: 1083 },
  'vorderkante-steinschlaege': { file: 'vorderkante-steinschlaege.jpg', thumb: 'vorderkante-steinschlaege-thumb.jpg', width: 1280, height: 593 },
  'vorderkante-steinschlaege-nah': { file: 'vorderkante-steinschlaege-nah.jpg', thumb: 'vorderkante-steinschlaege-nah-thumb.jpg', width: 1280, height: 900 },
  'tankdeckel-kratzer': { file: 'tankdeckel-kratzer.jpg', thumb: 'tankdeckel-kratzer-thumb.jpg', width: 960, height: 1280 },
  'tankdeckel-uebersicht': { file: 'tankdeckel-uebersicht.jpg', thumb: 'tankdeckel-uebersicht-thumb.jpg', width: 960, height: 1280 },
  'haube-verkleidung-abplatzer': { file: 'haube-verkleidung-abplatzer.jpg', thumb: 'haube-verkleidung-abplatzer-thumb.jpg', width: 1280, height: 647 },
  'trittflaeche-kratzer': { file: 'trittflaeche-kratzer.jpg', thumb: 'trittflaeche-kratzer-thumb.jpg', width: 1280, height: 1000 },
  'schraube-kratzer': { file: 'schraube-kratzer.jpg', thumb: 'schraube-kratzer-thumb.jpg', width: 960, height: 1280 },
  'rumpfunterkante-abplatzer': { file: 'rumpfunterkante-abplatzer.jpg', thumb: 'rumpfunterkante-abplatzer-thumb.jpg', width: 1280, height: 1125 },
  'kratzer-fluegel-hand': { file: 'kratzer-fluegel-hand.jpg', thumb: 'kratzer-fluegel-hand-thumb.jpg', width: 1280, height: 1125 },
  'kratzer-fluegel-nah': { file: 'kratzer-fluegel-nah.jpg', thumb: 'kratzer-fluegel-nah-thumb.jpg', width: 960, height: 1280 },
  'trittmarke-abgenutzt': { file: 'trittmarke-abgenutzt.jpg', thumb: 'trittmarke-abgenutzt-thumb.jpg', width: 1280, height: 1083 },
} as const satisfies Record<string, DemoPhoto>;

export type DemoPhotoName = keyof typeof DEMO_PHOTOS;

/** Where the web serves the files (same origin, CSP `img-src 'self'`). */
export const DEMO_PHOTO_PATH = '/demo-photos/';

/** File name → URL. The web's (and the default): the public path. */
export type DemoPhotoUrl = (file: string) => string;
export const webDemoPhotoUrl: DemoPhotoUrl = (file) => `${DEMO_PHOTO_PATH}${encodeURIComponent(file)}`;

const PREFIX = 'photo:';

export function demoPhotoRef(name: DemoPhotoName, thumb = false): string {
  return `${PREFIX}${name}${thumb ? ':thumb' : ''}`;
}

/** The photo a `photo:` ref names, or null for any other ref (and for an unknown name). */
export function parseDemoPhotoRef(ref: string): { photo: DemoPhoto; thumb: boolean } | null {
  if (!ref.startsWith(PREFIX)) return null;
  const [name = '', variant] = ref.slice(PREFIX.length).split(':');
  if (!Object.hasOwn(DEMO_PHOTOS, name)) return null;
  return { photo: DEMO_PHOTOS[name as DemoPhotoName], thumb: variant === 'thumb' };
}
